import {Hidden} from '@mui/material';
import AppConfirmDialog from 'main/core/AppConfirmDialog';
import AppsContainer from 'main/core/AppsContainer';
import AppsContent from 'main/core/AppsContainer/AppsContent';
import AppsHeader from 'main/core/AppsContainer/AppsHeader';
import AppsPagination from 'main/core/AppsPagination';
import {useWidget} from 'main/utility/hooks/useWidget';
import PropTypes from 'prop-types';
import {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {getWidgetDataList, modifyBatchWidgetData} from 'redux/actions';
import {UiWiseWidgetType} from 'shared/constants/UiWiseConst';
import TableList from './TableContent/TableList';
import TableNavBar from './TableNavBar';

const TableWidget = ({widgetSpec, isSingleWidget, params}) => {
  const dispatch = useDispatch();
  const {
    widgetid,
    widgettitle,
    fields,
    widgettype,
    idfieldname,
    showpagination,
    showsearch,
    actions,
    showexport,
  } = widgetSpec;
  const {widgetData, isLoading, isError} = useWidget(widgetid);

  const initialColumns = useMemo(() => {
    return fields.map((column) => ({
      name: column.name,
      label: column.label,
      show: column.visibility === 'show',
      showable: column.visibility === 'show',
    }));
  }, [fields]);

  const {rows = [], totalRows = 0} = widgetData || {};
  const isTableView = UiWiseWidgetType.TABLE_LIST === widgettype;
  const initialPageSize = showpagination
    ? isSingleWidget
      ? 15
      : 10
    : rows?.length || 100;

  const [tableParams, setTableParams] = useState({
    search: '',
    page: 0,
    sortColumn: idfieldname,
    sortOrder: 'asc',
    pageSize: initialPageSize,
  });

  const [checkedRows, setCheckedRows] = useState([]);
  const [dataColumns, setDataColumns] = useState(initialColumns);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [activeAlertId, setActiveAlertId] = useState(null);

  const refreshTable = (overrides = {}) => {
    const nextTableParams = {
      ...tableParams, // Current values (search, page, sort, etc.)
      ...overrides, // New values (the click event data)
    };
    setTableParams(nextTableParams);

    const apiPayload = {
      ...nextTableParams,
      ...params, // This adds your external filters/IDs to the request
    };
    dispatch(getWidgetDataList(widgetid, apiPayload));
  };

  useEffect(() => {
    refreshTable(params); // Initial loading based per widget, pass on params if there are any
  }, [widgetid, params]);

  const handleRequestSort = (_, property) => {
    const isAsc =
      tableParams.sortColumn === property && tableParams.sortOrder === 'asc';
    refreshTable({
      sortColumn: property,
      sortOrder: isAsc ? 'desc' : 'asc',
      page: 0,
    });
  };

  const onPageChange = (_, value) => {
    refreshTable({page: value});
  };

  const onSearchTextChange = (e) => {
    const nextText = e.target.value;
    setCheckedRows([]);
    refreshTable({search: nextText, page: 0});
  };

  function onColumnVisibilityChange(event) {
    const columnName = event.target.name;
    const newDataColumnsState = dataColumns.map((column) => {
      if (column.name === columnName) {
        return {
          ...column,
          show: !column.show,
        };
      } else {
        return column;
      }
    });
    setDataColumns(newDataColumnsState);
  }

  function onSelectAllColumnsVisibility(showAllColumns) {
    const newDataColumnsState = dataColumns.map((column) => {
      return {
        ...column,
        show: column.showable ? showAllColumns : false,
      };
    });
    setDataColumns(newDataColumnsState);
  }

  const onChangeCheckedRows = (event, id) => {
    if (event.target.checked) {
      setCheckedRows((prev) => [...prev, id]);
    } else {
      setCheckedRows((prev) => prev.filter((rowId) => rowId !== id));
    }
  };

  const showDialog = (alertId) => {
    setActiveAlertId(alertId);
    setDialogOpen(true);
  };

  const onDialogConfirm = (alertFormValues) => {
    let apiPayload = {
      idFieldName: idfieldname,
      ids: checkedRows,
      ...alertFormValues,
    };

    dispatch(modifyBatchWidgetData(activeAlertId, apiPayload)).then(() => {
      const shouldJumpBack = rows?.length <= 1 && tableParams.page > 0;
      const nextParams = shouldJumpBack
        ? {page: Math.max(tableParams.page - 1, 0)}
        : {}; // Use existing params
      refreshTable(nextParams);
    });

    onDialogCancel();
    setCheckedRows([]);
  };

  const onDialogCancel = () => {
    setActiveAlertId(null);
    setDialogOpen(false);
  };

  const showNavBar =
    widgettitle?.length > 0 ||
    showsearch ||
    showpagination ||
    showexport ||
    actions?.length > 0;

  return (
    <AppsContainer fullView isSingleWidget={isSingleWidget}>
      {showNavBar && (
        <AppsHeader>
          <TableNavBar
            widgetSpec={widgetSpec}
            tableParams={tableParams}
            params={params}
            rows={rows}
            totalRows={totalRows}
            checkedRows={checkedRows}
            showDialog={showDialog}
            dataColumns={dataColumns}
            onColumnVisibilityChange={onColumnVisibilityChange}
            onSelectAllColumnsVisibility={onSelectAllColumnsVisibility}
            onSearchTextChange={onSearchTextChange}
            onPageChange={onPageChange}
            disabled={isLoading}
          />
        </AppsHeader>
      )}

      <AppsContent
        sx={{
          paddingTop: 2.5,
          paddingBottom: 2.5,
          overflowY: 'auto',
        }}
      >
        {isTableView && (
          <TableList
            widgetSpec={widgetSpec}
            tableParams={tableParams}
            dataColumns={dataColumns}
            rows={rows}
            checkedRows={checkedRows}
            setCheckedRows={setCheckedRows}
            onChangeCheckedRows={onChangeCheckedRows}
            onRequestSort={handleRequestSort}
            isLoading={isLoading}
            isError={isError}
          />
        )}
      </AppsContent>

      {showpagination && (
        <Hidden smUp>
          <AppsPagination
            pageSize={tableParams.pageSize}
            totalRows={totalRows}
            page={tableParams.page}
            onPageChange={onPageChange}
          />
        </Hidden>
      )}

      {isDialogOpen && activeAlertId && (
        <AppConfirmDialog
          widgetid={activeAlertId}
          isOpen={isDialogOpen}
          onConfirm={onDialogConfirm}
          onClose={onDialogCancel}
        />
      )}
    </AppsContainer>
  );
};

TableWidget.propTypes = {
  widgetSpec: PropTypes.object.isRequired,
  isSingleWidget: PropTypes.bool.isRequired,
  params: PropTypes.object,
};

export default TableWidget;
